import { useQuery } from "@apollo/client"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDebounce } from "use-debounce"
import { gql } from "~/__generated__"
import { ContactTableFragment } from "~/__generated__/graphql"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { invitationsPath } from "~/common/paths"
import { ContactUpdateDialog } from "~/contacts/contact-update-dialog"
import { InvitationCreateDialog } from "~/invitations/invitation-create-dialog"
import { ContactTagCell } from "~/contacts/contact-tag-cell"
import { ContactActionsCell } from "~/contacts/contact-actions-cell"
import { ContactCSVExport } from "~/contacts/contact-csv-export"
import { WelcomeModal } from "~/contacts/welcome-modal"
import { useWelcomeFlow } from "~/contacts/use-welcome-flow"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { PostalGradientBar } from "~/ui/postal-gradient-bar"
import {
  MobileCardContent,
  MobileCardHeader,
  MobileCardRow,
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHeader,
  ResponsiveTableHeaderCell,
  ResponsiveTableRow,
} from "~/ui/responsive-table"
import { SearchInput } from "~/ui/search-input"
import { MobileSearchButton } from "~/ui/mobile-search-button"
import { TagFilter } from "~/ui/tag-filter"
import { HeaderWithTableFilters } from "~/ui/header-with-table-filters"
import { FilteredEmptyState } from "~/ui/filtered-empty-state"
import { TableTabs } from "~/ui/table-tabs"
import { useToast } from "~/ui/use-toast"
import { WalletDisplay } from "~/ui/wallet-display"
import { UserAvatar } from "~/users/user-avatar"
import loaderIcon from "~/images/icons/loader-icon"
import mailIcon from "~/images/icons/mail"
import smartphoneIcon from "~/images/icons/smartphone"
import slidersHorizontalIcon from "~/images/icons/sliders-horizontal"
import tagIcon from "~/images/icons/tag"
import tableIcon from "~/images/icons/table"

enum ActiveTab {
  Alphabetical = "alphabetical",
  RecentlyAdded = "recent",
}

gql(/* GraphQL */ `
  fragment ContactTable on Contact {
    id
    createdAt
    updatedAt
    user {
      id
      firstName
      lastName
      fullName
      handle
      email
      avatarThumbUrl
      emails {
        id
        email
      }
      phones {
        id
        phoneFormatted
      }
      wallets {
        id
        publicKey
        publicKeyFormatted
      }
      visibilityContactIndex
    }
    tags {
      id
      name
    }
  }
`)

const query = gql(/* GraphQL */ `
  query GetContacts($first: Int, $after: String, $filters: ContactFiltersInput) {
    viewer {
      tags {
        id
        name
      }
    }
    contacts(first: $first, after: $after, filters: $filters) {
      edges {
        node {
          ...ContactTable
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`)

const deleteContactMutation = gql(/* GraphQL */ `
  mutation DeleteContact($input: ContactDeleteInput!) {
    contactDelete(input: $input) {
      success
    }
  }
`)

export function ContactsScreen() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.Alphabetical)
  const [searchValue, setSearchValue] = useState("")
  const [debouncedSearchValue] = useDebounce(searchValue, 500)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<ContactTableFragment | null>(null)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)

  const { showModal: welcomeModalOpen, inviterName, clearWelcome } = useWelcomeFlow()

  const filters = useMemo(() => {
    const baseFilters: any = {}

    if (activeTab === ActiveTab.RecentlyAdded) {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      baseFilters.createdAtSince = sevenDaysAgo
    }

    if (debouncedSearchValue.trim()) {
      baseFilters.search = debouncedSearchValue.trim()
    }

    if (selectedTags.length > 0) {
      baseFilters.tags = selectedTags
    }

    return Object.keys(baseFilters).length > 0 ? baseFilters : null
  }, [activeTab, debouncedSearchValue, selectedTags])

  const { data, previousData, loading, error, fetchMore } = useQuery(query, {
    variables: {
      first: 50,
      after: null,
      filters,
    },
    notifyOnNetworkStatusChange: true,
  })

  const contacts = (data ?? previousData)?.contacts?.edges?.map((edge) => edge.node!) || []
  const allTags = (data ?? previousData)?.viewer?.tags || []

  const handleOpenUpdateDialog = (contact: ContactTableFragment) => {
    setSelectedContact(contact)
    setUpdateDialogOpen(true)
  }

  const handleRowClick = (contact: ContactTableFragment) => {
    if (contact.user.handle) {
      navigate(`/${contact.user.handle}`)
    }
  }

  const handleCreateInvite = () => {
    setInviteDialogOpen(true)
  }

  const handleInviteSuccess = () => {
    navigate(invitationsPath({}))
  }

  const [deleteContact] = useSafeMutation(deleteContactMutation)

  const handleRemoveContact = async (contact: ContactTableFragment) => {
    const userName =
      contact.user.fullName ||
      `${contact.user.firstName || ""} ${contact.user.lastName || ""}`.trim() ||
      contact.user.handle ||
      "this contact"

    const confirmed = window.confirm(
      `Are you sure you want to remove ${userName}? This will remove ${userName} from your Contacts. You will have to search and re-add them back if you change your mind.`
    )

    if (!confirmed) {
      return
    }

    try {
      const result = await deleteContact({
        variables: {
          input: {
            id: contact.id,
          },
        },
        refetchQueries: ["GetContacts"],
      })

      if (result.data?.contactDelete?.success) {
        toast({
          title: "Contact removed",
          description: "The contact has been successfully removed.",
        })
      }
    } catch (err) {
      console.error("Error removing contact:", err)
      toast({
        title: "Error",
        description: "Failed to remove contact. Please try again.",
        variant: "destructive",
      })
    }
  }

  if (error) {
    return <GraphqlError error={error} />
  }

  return (
    <div>
      <PostalGradientBar />
      <div className="px-4 py-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <HeaderWithTableFilters
            title="Contacts"
            subtitle="Index view of Contacts"
            selectedTags={selectedTags}
            allTags={allTags}
            onTagRemove={(tagId) => setSelectedTags(selectedTags.filter((id) => id !== tagId))}
            onClearAll={() => setSelectedTags([])}
          />
          <div className="flex items-center gap-3">
            <Button onClick={handleCreateInvite}>Create an Invite</Button>
            <ContactCSVExport
              contacts={contacts}
              fileName={`contacts${selectedTags.length > 0 ? "-filtered" : ""}`}
            />
          </div>
        </div>

        <div className="border-b">
          <div className="flex items-center justify-between">
            <TableTabs
              tabs={[
                { id: ActiveTab.Alphabetical, label: "Alphabetical", icon: tableIcon },
                { id: ActiveTab.RecentlyAdded, label: "Recently Added", icon: tableIcon },
              ]}
              activeTab={activeTab}
              onTabChange={(tabId) => setActiveTab(tabId as ActiveTab)}
            />
            <div className="flex items-center gap-4">
              <TagFilter
                tags={allTags}
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
              />
              {/* Desktop search */}
              <div className="hidden md:block">
                <SearchInput
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search contacts..."
                />
              </div>
              {/* Mobile search button */}
              <div className="md:hidden">
                <MobileSearchButton onClick={() => setMobileSearchOpen(!mobileSearchOpen)} />
              </div>
            </div>
          </div>
          {/* Mobile search bar (shown when search button is clicked) */}
          {mobileSearchOpen && (
            <div className="border-t p-4 md:hidden">
              <SearchInput
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search contacts..."
              />
            </div>
          )}
        </div>

        {loading && !data ? (
          <div className="flex justify-center py-8">
            <img {...loaderIcon} alt="Loading..." className="animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <FilteredEmptyState
            selectedTags={selectedTags}
            allTags={allTags}
            onClearFilters={() => setSelectedTags([])}
            itemType="contacts"
            emptyTitle="No Contacts"
            emptyDescription="You don't have any contacts yet. Create an invite to start building your network!"
            emptyAction={<Button onClick={handleCreateInvite}>Create an Invite</Button>}
            filteredAction={
              <Button variant="outline" onClick={handleCreateInvite}>
                Create an Invite
              </Button>
            }
          />
        ) : (
          <ResponsiveTable className="mt-4 md:mt-0">
            <ResponsiveTableHeader>
              <ResponsiveTableHeaderCell span={2}>Name & Handle</ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={2}>
                <div className="flex items-center gap-2">
                  <img {...mailIcon} alt="" />
                  Email
                </div>
              </ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={2}>
                <div className="flex items-center gap-2">
                  <img {...smartphoneIcon} alt="" />
                  Phone
                </div>
              </ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={2}>Wallets</ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={3}>
                <div className="flex items-center gap-2">
                  <img src={tagIcon.src} alt="" className="h-4 w-4" />
                  Tags
                </div>
              </ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={1}>
                <div className="flex items-center gap-2">
                  <img {...slidersHorizontalIcon} alt="" />
                  Actions
                </div>
              </ResponsiveTableHeaderCell>
            </ResponsiveTableHeader>

            <ResponsiveTableBody>
              {contacts.map((contact) => {
                const user = contact.user
                const primaryEmail = user.emails?.[0]?.email || ""
                const primaryPhone = user.phones?.[0]?.phoneFormatted || ""

                return (
                  <ResponsiveTableRow
                    key={contact.id}
                    onClick={() => handleRowClick(contact)}
                    desktopContent={
                      <>
                        <ResponsiveTableCell span={2}>
                          <div className="flex items-center gap-3">
                            <UserAvatar user={user} className="h-8 w-8 flex-shrink-0" />
                            <div className="flex min-w-0 flex-1 flex-col">
                              <div className="truncate font-medium">
                                {user.fullName ||
                                  `${user.firstName || ""} ${user.lastName || ""}`.trim()}
                              </div>
                              {user.handle && (
                                <div className="truncate text-sm text-muted-foreground">
                                  @{user.handle}
                                </div>
                              )}
                            </div>
                          </div>
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={2}>
                          <div className="truncate">
                            {primaryEmail || (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </div>
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={2}>
                          <div className="truncate">
                            {primaryPhone || (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </div>
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={2}>
                          <div className="space-y-1">
                            {user.wallets && user.wallets.length > 0 ? (
                              user.wallets.map((wallet) => (
                                <WalletDisplay
                                  key={wallet.id}
                                  publicKey={wallet.publicKey}
                                  publicKeyFormatted={wallet.publicKeyFormatted}
                                  className="text-xs"
                                />
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </div>
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={3}>
                          <ContactTagCell contact={contact} onEditTags={handleOpenUpdateDialog} />
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={1}>
                          <ContactActionsCell contact={contact} onRemove={handleRemoveContact} />
                        </ResponsiveTableCell>
                      </>
                    }
                    mobileContent={
                      <>
                        <MobileCardHeader>
                          <div className="flex items-center gap-3">
                            <UserAvatar user={user} className="h-8 w-8 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium">
                                {user.fullName ||
                                  `${user.firstName || ""} ${user.lastName || ""}`.trim()}
                              </div>
                              {user.handle && (
                                <div className="truncate text-sm text-muted-foreground">
                                  @{user.handle}
                                </div>
                              )}
                            </div>
                          </div>
                        </MobileCardHeader>

                        <MobileCardContent>
                          <MobileCardRow label="Email">
                            {primaryEmail || (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </MobileCardRow>

                          <MobileCardRow label="Phone">
                            {primaryPhone || (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </MobileCardRow>

                          <MobileCardRow label="Wallets">
                            <div className="space-y-1">
                              {user.wallets && user.wallets.length > 0 ? (
                                user.wallets.map((wallet) => (
                                  <WalletDisplay
                                    key={wallet.id}
                                    publicKey={wallet.publicKey}
                                    publicKeyFormatted={wallet.publicKeyFormatted}
                                    className="text-xs"
                                  />
                                ))
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </div>
                          </MobileCardRow>

                          <MobileCardRow label="Tags">
                            <ContactTagCell contact={contact} onEditTags={handleOpenUpdateDialog} />
                          </MobileCardRow>

                          <MobileCardRow label="Actions">
                            <ContactActionsCell contact={contact} onRemove={handleRemoveContact} />
                          </MobileCardRow>
                        </MobileCardContent>
                      </>
                    }
                  />
                )
              })}
            </ResponsiveTableBody>
          </ResponsiveTable>
        )}

        {/* Load More / Pagination */}
        {data?.contacts?.pageInfo.hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              onClick={() => {
                fetchMore({
                  variables: {
                    after: data?.contacts?.pageInfo.endCursor,
                  },
                })
              }}
            >
              Load more
            </Button>
          </div>
        )}

        {selectedContact && (
          <ContactUpdateDialog
            open={updateDialogOpen}
            onOpenChange={setUpdateDialogOpen}
            contact={selectedContact}
          />
        )}

        <InvitationCreateDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          redirectOnSuccess={handleInviteSuccess}
        />

        <WelcomeModal
          open={welcomeModalOpen}
          onOpenChange={clearWelcome}
          inviterName={inviterName}
        />
      </div>
    </div>
  )
}
